import { Experiment, ExperimentRolloutDurability, ExperimentRolloutStatus } from "@/http/api-types";

export type ExperimentRolloutOption = {
  value: ExperimentRolloutStatus;
  label: string;
  description: string;
  badgeVariant: "rollout_rolled_out" | "rollout_follow_up" | "rollout_learning";
};

export const EXPERIMENT_ROLLOUT_OPTIONS: ExperimentRolloutOption[] = [
  {
    value: ExperimentRolloutStatus.RolledOut,
    label: "Rolled out",
    description: "The experiment result was rolled out to the intended production scope.",
    badgeVariant: "rollout_rolled_out",
  },
  {
    value: ExperimentRolloutStatus.CreatedFollowUpExperiment,
    label: "Created follow up experiment",
    description: "The result led to a follow-up experiment instead of an immediate rollout.",
    badgeVariant: "rollout_follow_up",
  },
  {
    value: ExperimentRolloutStatus.LearningDocumented,
    label: "Learning documented",
    description: "The rollout decision is captured as a documented learning.",
    badgeVariant: "rollout_learning",
  },
];

const EXPERIMENT_ROLLOUT_OPTIONS_BY_VALUE: Record<ExperimentRolloutStatus, ExperimentRolloutOption> = {
  [ExperimentRolloutStatus.RolledOut]: EXPERIMENT_ROLLOUT_OPTIONS[0],
  [ExperimentRolloutStatus.CreatedFollowUpExperiment]: EXPERIMENT_ROLLOUT_OPTIONS[1],
  [ExperimentRolloutStatus.LearningDocumented]: EXPERIMENT_ROLLOUT_OPTIONS[2],
};

export function getExperimentRolloutOption(
  status: ExperimentRolloutStatus | undefined,
): ExperimentRolloutOption | undefined {
  if (!status) {
    return undefined;
  }
  return EXPERIMENT_ROLLOUT_OPTIONS_BY_VALUE[status];
}

export type ExperimentRolloutDurabilityOption = {
  value: ExperimentRolloutDurability;
  label: string;
  description: string;
  badgeVariant: "durability_still_live" | "durability_reverted" | "durability_churned";
  requiresNote: boolean;
  // Label for rollout_durability_ended_at, and undefined when this outcome has no end
  // date at all. Still live has not stopped, so there is no moment to ask for or show —
  // the column still gets the write instant, but surfacing it would just repeat
  // "Checked on". Reverted and churned each name their own event instead of sharing a
  // generic one, because "Ended at" would read against the experiment's own End Date.
  endedAtLabel?: string;
};

export const EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS: ExperimentRolloutDurabilityOption[] = [
  {
    value: ExperimentRolloutDurability.StillLive,
    label: "Still live",
    description: "The rolled-out change is still running in production.",
    badgeVariant: "durability_still_live",
    requiresNote: false,
  },
  {
    value: ExperimentRolloutDurability.Reverted,
    label: "Reverted",
    description: "The rolled-out change was rolled back.",
    badgeVariant: "durability_reverted",
    requiresNote: true,
    endedAtLabel: "Reverted at",
  },
  {
    value: ExperimentRolloutDurability.ClientChurned,
    label: "Client churned",
    description: "The change may still be live, but there is no traffic left to judge it by.",
    badgeVariant: "durability_churned",
    requiresNote: true,
    endedAtLabel: "Client churned at",
  },
];

const EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS_BY_VALUE: Record<
  ExperimentRolloutDurability,
  ExperimentRolloutDurabilityOption
> = {
  [ExperimentRolloutDurability.StillLive]: EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS[0],
  [ExperimentRolloutDurability.Reverted]: EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS[1],
  [ExperimentRolloutDurability.ClientChurned]: EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS[2],
};

// Returns undefined when nobody has recorded an outcome yet. Callers must render that
// case as "not confirmed" rather than falling back to Still live — an unconfirmed
// rollout reading as a confirmed one is the exact failure this feature exists to fix.
export function getExperimentRolloutDurabilityOption(
  durability: ExperimentRolloutDurability | undefined,
): ExperimentRolloutDurabilityOption | undefined {
  if (!durability) {
    return undefined;
  }
  return EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS_BY_VALUE[durability];
}

// The durability outcome only applies to a change that actually shipped. A follow-up
// experiment or a documented learning has nothing live to stay live or be reverted, and
// the API rejects those, so the UI must not offer it for them either.
export function canRecordRolloutDurability(experiment: Pick<Experiment, "rollout_status" | "rollout_at">): boolean {
  return experiment.rollout_status === ExperimentRolloutStatus.RolledOut && Boolean(experiment.rollout_at);
}

export function durabilityRequiresNote(durability: ExperimentRolloutDurability | ""): boolean {
  return durability !== "" && Boolean(getExperimentRolloutDurabilityOption(durability)?.requiresNote);
}

// A confirmation is a point-in-time observation, not a permanent state: "Still live"
// recorded in March says nothing about today. These thresholds decide when the badge
// stops claiming the confidence it had on the day it was recorded. 90 days matches the
// window the rollout durability gate measures a revert against.
export const ROLLOUT_DURABILITY_AGING_AFTER_DAYS = 30;
export const ROLLOUT_DURABILITY_STALE_AFTER_DAYS = 90;

export type ExperimentRolloutDurabilityFreshnessLevel = "fresh" | "aging" | "stale";

export type ExperimentRolloutDurabilityFreshness =
  | {
      isTracked: true;
      level: ExperimentRolloutDurabilityFreshnessLevel;
      ageDays: number;
      confirmedAt: string;
    }
  | { isTracked: false };

const UNTRACKED: ExperimentRolloutDurabilityFreshness = { isTracked: false };

export type ExperimentRolloutDurabilityFreshnessInput = Pick<
  Experiment,
  "rollout_durability_status" | "rollout_durability_recorded_at"
>;

// Freshness is keyed on rollout_durability_recorded_at, not rollout_durability_ended_at.
// recorded_at is stamped by the server on every write, so it always moves when someone
// re-confirms and cannot be backdated by the client. ended_at does not even exist for a
// still-live rollout, which is the only outcome whose freshness decays.
//
// Only Still live decays. Reverted and Client churned are terminal facts about something
// that already happened; re-checking them tells you nothing new.
export function getExperimentRolloutDurabilityFreshness(
  experiment: ExperimentRolloutDurabilityFreshnessInput,
  now: Date,
): ExperimentRolloutDurabilityFreshness {
  if (experiment.rollout_durability_status !== ExperimentRolloutDurability.StillLive) {
    return UNTRACKED;
  }

  const recordedAt = experiment.rollout_durability_recorded_at
    ? new Date(experiment.rollout_durability_recorded_at)
    : null;
  if (!recordedAt || Number.isNaN(recordedAt.getTime())) {
    return UNTRACKED;
  }

  // A clock skew or a backdated import can put the record marginally in the future.
  // Clamping to 0 keeps that reading as "just confirmed" rather than wrapping to a
  // negative age that would sort above everything.
  const ageDays = Math.max(0, Math.floor((now.getTime() - recordedAt.getTime()) / 86_400_000));

  let level: ExperimentRolloutDurabilityFreshnessLevel = "fresh";
  if (ageDays >= ROLLOUT_DURABILITY_STALE_AFTER_DAYS) {
    level = "stale";
  } else if (ageDays >= ROLLOUT_DURABILITY_AGING_AFTER_DAYS) {
    level = "aging";
  }

  return { isTracked: true, level, ageDays, confirmedAt: recordedAt.toISOString() };
}

// Deliberately not colour-only: the age rides in the badge label so the signal survives
// a colour-blind reader and answers "how stale" without needing the tooltip.
export function formatRolloutDurabilityAge(ageDays: number): string {
  if (ageDays <= 0) {
    return "today";
  }
  return `${ageDays}d`;
}

const FRESHNESS_BADGE_VARIANTS: Record<
  ExperimentRolloutDurabilityFreshnessLevel,
  "durability_still_live" | "durability_still_live_aging" | "durability_still_live_stale"
> = {
  fresh: "durability_still_live",
  aging: "durability_still_live_aging",
  stale: "durability_still_live_stale",
};

export function getRolloutDurabilityFreshnessBadgeVariant(
  level: ExperimentRolloutDurabilityFreshnessLevel,
): "durability_still_live" | "durability_still_live_aging" | "durability_still_live_stale" {
  return FRESHNESS_BADGE_VARIANTS[level];
}

// Reverted and churned are the outcomes that stopped a rollout being live, so they carry
// an end date the durability gate measures survival against. Still live has not stopped.
export function durabilityRecordsEndDate(durability: ExperimentRolloutDurability | "" | undefined): boolean {
  return Boolean(durability && getExperimentRolloutDurabilityOption(durability)?.endedAtLabel);
}

// Mirrors DurabilityDays in backend/evidencegates/check1.go: how long a rollout has to
// survive before the durability gate counts it. Kept here so the tooltip can say whether
// a revert landed inside the window rather than making the reader subtract dates.
export const ROLLOUT_DURABILITY_PROVEN_DAYS = 90;

export type RolloutLifespan = {
  days: number;
  provenDurable: boolean;
};

export type RolloutLifespanInput = Pick<
  Experiment,
  "rollout_at" | "rollout_durability_status" | "rollout_durability_ended_at"
>;

// How long the change was actually live: rollout_at to the moment it stopped. Only
// defined for the outcomes that ended it — a still-live rollout has no end to measure to,
// and its badge already reports how stale the last check is.
export function getRolloutLifespan(experiment: RolloutLifespanInput): RolloutLifespan | undefined {
  if (!durabilityRecordsEndDate(experiment.rollout_durability_status)) {
    return undefined;
  }

  const rolledOutAt = experiment.rollout_at ? new Date(experiment.rollout_at) : null;
  const endedAt = experiment.rollout_durability_ended_at ? new Date(experiment.rollout_durability_ended_at) : null;
  if (!rolledOutAt || !endedAt || Number.isNaN(rolledOutAt.getTime()) || Number.isNaN(endedAt.getTime())) {
    return undefined;
  }

  // The API and the DB both reject an end before the rollout, so a negative span means
  // bad data rather than a real ordering; clamping keeps it from rendering as "-5d".
  const days = Math.max(0, Math.floor((endedAt.getTime() - rolledOutAt.getTime()) / 86_400_000));
  return { days, provenDurable: days >= ROLLOUT_DURABILITY_PROVEN_DAYS };
}
